import { searchQueryKeys } from '@features/search/api/queryKeys'
import { useQuery, type QueryClient } from '@tanstack/react-query'
import { searchGlobal, searchPreview, type SearchMatchParams } from '../../search/api/search.api'

const SEARCH_PREVIEW_STALE_TIME = 60_000

export function useGlobalSearch(query: string, matchParams: SearchMatchParams = {}) {
  return useQuery({
    queryKey: searchQueryKeys.searchGlobal(query, matchParams),
    queryFn: ({ signal }) => searchGlobal(query, 50, matchParams, signal),
    enabled: query.trim().length > 0,
    staleTime: 30_000,
  })
}

export function getSearchPreviewQueryOptions(hydrate = false) {
  return {
    queryKey: searchQueryKeys.searchPreview(hydrate),
    queryFn: () => searchPreview(hydrate),
    staleTime: SEARCH_PREVIEW_STALE_TIME,
  }
}

/**
 * Warms the quick preview the palette opens on. The hydrated one counts every
 * table's records, so it waits for the palette to be open (useSearchPreview).
 */
export function prefetchSearchPreview(queryClient: QueryClient) {
  return queryClient.prefetchQuery(getSearchPreviewQueryOptions(false))
}

export function invalidateSearchPreview(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: searchQueryKeys.searchPreviewAll })
}

/** Query-less preview for the empty search box. Only fetched while `enabled`
 * (i.e. the palette is open with no query), and cached briefly. */
export function useSearchPreview(enabled: boolean) {
  const initial = useQuery({
    ...getSearchPreviewQueryOptions(false),
    enabled,
  })

  const hydrated = useQuery({
    ...getSearchPreviewQueryOptions(true),
    enabled: enabled && initial.isSuccess,
  })

  return {
    ...initial,
    data: hydrated.data ?? initial.data,
    isLoading: initial.isLoading,
    isFetching: initial.isFetching || hydrated.isFetching,
  }
}
